import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Clock, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const defaultSchedule = () => {
  const s = {};
  for (let i = 0; i < 7; i++) s[i] = { start: '09:00', end: '18:00', off: i === 6 };
  return s;
};

const WorkScheduleModal = ({ open, onClose, personnelList = [], onSaved }) => {
  const [pid, setPid] = useState('');
  const [schedule, setSchedule] = useState(defaultSchedule());
  const [breakLimit, setBreakLimit] = useState(60);
  const [managerPhone, setManagerPhone] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/settings/notifications`, { headers: authHeader() })
        .then(r => setManagerPhone(r.data?.manager_phone || ''))
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (!pid) return;
    const p = personnelList.find(x => x.id === pid);
    if (p && p.work_schedule) {
      const norm = {};
      for (let i = 0; i < 7; i++) {
        const d = p.work_schedule[String(i)] || p.work_schedule[i];
        norm[i] = d ? { start: d.start || '09:00', end: d.end || '18:00', off: !!d.off } : { start: '09:00', end: '18:00', off: i === 6 };
      }
      setSchedule(norm);
    } else {
      setSchedule(defaultSchedule());
    }
    setBreakLimit((p && p.break_limit_minutes) || 60);
  }, [pid, personnelList]);

  const updateDay = (i, field, value) => {
    setSchedule(prev => ({ ...prev, [i]: { ...prev[i], [field]: value } }));
  };

  const save = async () => {
    if (!pid) { toast.error('Personel seçin'); return; }
    setSaving(true);
    try {
      const ws = {};
      for (let i = 0; i < 7; i++) ws[String(i)] = schedule[i];
      await axios.put(`${API}/personnel/${pid}/work-schedule`, { work_schedule: ws, break_limit_minutes: Number(breakLimit) || 60 }, { headers: authHeader() });
      await axios.put(`${API}/settings/notifications`, { manager_phone: managerPhone.trim() }, { headers: authHeader() });
      toast.success('Çalışma saatleri kaydedildi');
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="work-schedule-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-700"><Clock className="w-5 h-5" /> Çalışma Saatleri</DialogTitle>
          <DialogDescription>Her personel için haftalık mesai programını belirleyin. Personel giriş/çıkışta bu saatlere göre geç/erken uyarısı alır ve açıklama yazar.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Personel *</Label>
            <select value={pid} onChange={(e) => setPid(e.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white" data-testid="ws-personnel-select">
              <option value="">Personel seçin...</option>
              {personnelList.map(p => (<option key={p.id} value={p.id}>{p.name} {p.position ? `— ${p.position}` : ''}</option>))}
            </select>
          </div>

          {pid && (
            <>
              <div className="border rounded-lg divide-y">
                {DAYS.map((d, i) => (
                  <div key={i} className={`flex items-center gap-3 px-3 py-2 ${schedule[i]?.off ? 'bg-slate-50' : ''}`} data-testid={`ws-day-${i}`}>
                    <div className="w-24 text-sm font-medium text-slate-700">{d}</div>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
                      <input type="checkbox" checked={!schedule[i]?.off} onChange={(e) => updateDay(i, 'off', !e.target.checked)} data-testid={`ws-day-active-${i}`} />
                      Çalışıyor
                    </label>
                    {!schedule[i]?.off ? (
                      <div className="flex items-center gap-2 ml-auto">
                        <Input type="time" value={schedule[i]?.start || '09:00'} onChange={(e) => updateDay(i, 'start', e.target.value)} className="h-8 w-28" data-testid={`ws-day-start-${i}`} />
                        <span className="text-slate-400">–</span>
                        <Input type="time" value={schedule[i]?.end || '18:00'} onChange={(e) => updateDay(i, 'end', e.target.value)} className="h-8 w-28" data-testid={`ws-day-end-${i}`} />
                      </div>
                    ) : (
                      <div className="ml-auto text-xs text-slate-400 italic pr-2">İzin günü / kapalı</div>
                    )}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Mola Limiti (dakika)</Label>
                  <Input type="number" min="1" value={breakLimit} onChange={(e) => setBreakLimit(e.target.value)} data-testid="ws-break-limit" />
                  <p className="text-[10px] text-slate-400 mt-1">Bu süreye 5 dk kala hatırlatma, aşılınca uyarı gönderilir.</p>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Yönetici Uyarı No</Label>
                  <Input value={managerPhone} onChange={(e) => setManagerPhone(e.target.value)} placeholder="905xxxxxxxxx" data-testid="ws-manager-phone" />
                  <p className="text-[10px] text-slate-400 mt-1">Mola aşımı uyarıları bu numaraya gider.</p>
                </div>
              </div>
            </>
          )}

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1" disabled={saving}>Kapat</Button>
            <Button onClick={save} disabled={saving || !pid} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="ws-save-btn">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkScheduleModal;
